import * as React from 'react'

import apis from '@app/api/api'
import useI18n from '@store/i18n/useI18n'
import styled from '@theme/styled-components'
import Swal from 'sweetalert2'

import Select from '@components/select/select'
import Loader from 'react-loader-spinner'
import theme from '@theme/Theme'

import AnimatedNumbers from 'react-animated-numbers'

interface Props {
  adminRegions: Option[]
  user_type_options: Option[]
}

const DISPLAYING_DURATIONS = [1, 7, 31, 182, 365, 3650]

const Statistics = ({ adminRegions, user_type_options }: Props) => {
  const i18n = useI18n()

  const [typeStats, setTypeStats] = React.useState<Option>()
  const [selectedRegion, setSelectedRegion] = React.useState<Option>()

  const [loading, setLoading] = React.useState<boolean>(false)

  const [displayingDatas, setDisplayingDatas] = React.useState<BusinessStatsSimplified[]>([])

  React.useEffect(() => {
    if (typeStats && selectedRegion) {
      const type = typeStats.value as UserType

      if (displayingDatas.length === 0) {
        setLoading(true)
      }
      Promise.all(
        DISPLAYING_DURATIONS.map((duration) =>
          apis.admin.export.stats({ type, daysSinceCreation: duration, regionId: selectedRegion.value })
        )
      )
        .then((res) => {
          setDisplayingDatas(res)
          setLoading(false)
        })
        .catch(() => {
          Swal.fire({
            title: i18n.t('components.adminData.errorexport.title'),
            text: i18n.t('components.adminData.errorexport.text'),
            icon: 'error',
            showCancelButton: false,
            confirmButtonColor: '#3A8641',
            confirmButtonText: i18n.t('components.adminData.errorexport.confirm'),
          })
          setLoading(false)
          setDisplayingDatas([])
        })
    } else {
      setDisplayingDatas([])
    }
  }, [typeStats, selectedRegion])

  return (
    <>
      <SectionTitleContainer>{i18n.t('components.adminData.sectionTitles.statistics')}</SectionTitleContainer>
      <RowContainer>
        <FormItemContainer>
          <SelectorContainer>
            <SelectorTitle>{i18n.t('components.adminData.type')}</SelectorTitle>
            <Select
              selected={typeStats}
              options={user_type_options}
              setSelected={setTypeStats}
              isClearable
              placeholder={i18n.t('components.adminData.placeholders.type')}
              noMargin
            />
          </SelectorContainer>
        </FormItemContainer>
        <FormItemContainer>
          <SelectorContainer>
            <SelectorTitle>{i18n.t('components.adminData.region')}</SelectorTitle>
            <Select
              selected={selectedRegion}
              options={adminRegions}
              setSelected={setSelectedRegion}
              isClearable
              placeholder={i18n.t('components.adminData.placeholders.region')}
              noMargin
            />
          </SelectorContainer>
        </FormItemContainer>
      </RowContainer>
      {loading ? (
        <CenteredContainer>
          <Loader type="ThreeDots" color={theme.colors.mapButtonLoader} height={100} />
        </CenteredContainer>
      ) : displayingDatas.length === 0 ? (
        <CenteredContainer>
          <NoDataLibelle>{i18n.t('components.adminData.noexport.text')}</NoDataLibelle>
        </CenteredContainer>
      ) : (
        <AllStatisticsContainer>
          {displayingDatas.map((itm, idx) => (
            <StatsContainer key={idx}>
              <StatisticsTitleContainer>
                {i18n.t(`components.adminData.sectionTitles.stats.${DISPLAYING_DURATIONS[idx]}`)}
              </StatisticsTitleContainer>
              <Separator />
              <StatsItem>
                <StatItem>
                  <StatNumberContainer>
                    <AnimatedNumbers
                      animateToNumber={itm.nbrDeclarations}
                      fontStyle={{ fontSize: 50, color: theme.colors.primary_turquoise }}
                      configs={[
                        { mass: 1, tension: 130, friction: 40 },
                        { mass: 2, tension: 140, friction: 40 },
                        { mass: 3, tension: 130, friction: 40 },
                      ]}
                    />
                  </StatNumberContainer>
                  <StatisticsTypeContainer>
                    {i18n.t(`components.adminData.sectionTitles.stats.nbrDeclarations`)}
                  </StatisticsTypeContainer>
                </StatItem>
                <StatItem>
                  <StatNumberContainer>
                    <AnimatedNumbers
                      animateToNumber={itm.nbrPhotos}
                      fontStyle={{ fontSize: 50, color: theme.colors.primary_emeraude }}
                      configs={[
                        { mass: 1, tension: 130, friction: 40 },
                        { mass: 2, tension: 140, friction: 40 },
                        { mass: 3, tension: 130, friction: 40 },
                      ]}
                    />
                  </StatNumberContainer>
                  <StatisticsTypeContainer>
                    {i18n.t(`components.adminData.sectionTitles.stats.nbrPhotos`)}
                  </StatisticsTypeContainer>
                </StatItem>
              </StatsItem>
            </StatsContainer>
          ))}
        </AllStatisticsContainer>
      )}
    </>
  )
}

export default Statistics

const SectionTitleContainer = styled('div')`
  ${(props) => props.theme.fonts.sectionTitle};
  margin: 10px;
  margin-top: 0;
`
const RowContainer = styled('div')`
  display: flex;
  align-items: center;
  margin: 10px;
  margin-top: 0;
`
const FormItemContainer = styled('div')<{ center?: boolean }>`
  display: flex;
  ${(props) => props.center && 'align-items: center'}
  flex: 1;
  margin: 10px;
  margin-top: 0;
`
const SelectorContainer = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;

  margin: 5px;
`
const SelectorTitle = styled('div')`
  ${(props) => props.theme.fonts.selectorTitle};
`
const CenteredContainer = styled('div')`
  margin: auto;
`
const NoDataLibelle = styled('div')`
  ${(props) => props.theme.fonts.label};
`
const AllStatisticsContainer = styled('div')`
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  justify-content: space-around;
`
const StatsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 350px;
  background-color: ${(props) => props.theme.colors.cardBackground};
  box-shadow: 1px 1px 2px 1px #2224;
  padding: 25px;
  border-radius: 15px;
  margin-bottom: 25px;
`
const StatsItem = styled('div')`
  display: flex;
  flex: 1;
  margin-top: 20px;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
  box-shadow: inset 0px 0px 2px 1px #2224;
  padding: 20px;
  border-radius: 15px;
`
const StatItem = styled('div')`
  align-items: center;
  justify-content: center;
  display: flex;
  flex: 1;
  flex-direction: column;
`
const StatNumberContainer = styled('div')`
  background-color: ${(props) => props.theme.colors.cardBackground};
  box-shadow: 1px 1px 2px 1px #2224;
  height: 100px;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 250px;
`
const StatisticsTitleContainer = styled('div')`
  display: flex;
  flex: 1;
  ${(props) => props.theme.fonts.sectionTitle};
  font-size: 15px;
`
const StatisticsTypeContainer = styled('div')`
  ${(props) => props.theme.fonts.label};
  color: ${(props) => props.theme.colors.dataMenuColor};
  font-size: 15px;
  margin-top: 15px;
  text-align: center;
`
const Separator = styled('div')`
  height: 2px;
  background-color: ${(props) => props.theme.colors.dataMenuColor};
  width: 100px;
  border-radius: 100px;
`
